/*eslint max-lines-per-function: ["error", 165]*/
import React, { ReactNode } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { Alert, Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';

import {
  CreateUpdateContractEstimateItemDto,
  GetContractEstimateItemDto,
  GetContractExtendedDto,
  GetEstimateItemContractDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import momencik from '../../../common/momencik';
import { useNavigation } from '../../../common/navigation';
import numberFormatter from '../../../common/numberFormatter';
import mathUtils from '../../../utils/mathUtils';
import CurrencyControl from '../../../app/components/CurrencyControl';

import { ContractEstimateItemGridRow } from './ContractEstimateItemGridRow';
import { getNotAssignedAmount, SumData } from './contractEstimateItemGridUtils';
import { ContractEstimateItemPositionsGridSum } from './ContractEstimateItemPositionsGridSum';

interface ContractEstimateItemGridProps {
  extendedContract?: GetContractExtendedDto;
  contractEstimateItems: CreateUpdateContractEstimateItemDto[];
  availableEstimateItems: GetEstimateItemContractDto[];
  readOnly: boolean;
  handleRefresh: () => void;
  handleAmountChange: (
    estimateItemId: string,
    item: CreateUpdateContractEstimateItemDto | undefined,
    amount: string | null | undefined,
  ) => void;
  warnings: string[];
  // sumData: SumData;
  handleFillAllAmounts: () => void;
  sumData: SumData;
}

export interface TableColumn {
  header: ReactNode;
  tdCssClass?: string;
  renderCell: (
    estimateItem: GetEstimateItemContractDto,
    contractEstimateItem: CreateUpdateContractEstimateItemDto,
    savedItem?: GetContractEstimateItemDto,
  ) => string | ReactNode | null;
}

const TableHead = ({ columns }: { columns: TableColumn[] }) => (
  <thead>
    <tr>
      {columns.map((col, index) => (
        <th key={`${col.header}-${index}`}>{col.header}</th>
      ))}
    </tr>
  </thead>
);

export const ContractEstimateItemGrid: React.FC<
  ContractEstimateItemGridProps
> = props => {
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();
  const nav = useNavigation();

  const columns: TableColumn[] = [
    {
      header: t(tk.prLang.estimateItemPosition),
      renderCell: (estimateItem, contractEstimateItem, savedItem) => (
        <>
          {estimateItem.position.name}
          {savedItem ? (
            <LastEditorInfo
              className="last-editor-inline"
              id={savedItem.id}
              method={pok.contractsEstimateItems.getLastEstimateItemEditor}
            />
          ) : null}
        </>
      ),
    },
    {
      header: t(tk.common.month),
      renderCell: estimateItem =>
        momencik(estimateItem.estimateByMonth.date, 'YYYY-MM'),
    },
    {
      header: t(tk.prLang.project),
      renderCell: estimateItem => (
        <>
          {estimateItem.estimateByMonth.project.name}
          {' ('}
          <Button
            variant="link"
            className="p-0 m-0"
            onClick={() =>
              nav.projectDetails(
                estimateItem.estimateByMonth.project.id,
                true,
                'estimates',
              )
            }
          >
            {estimateItem.estimateByMonth.project.number}
          </Button>
          {')'}
        </>
      ),
    },
    {
      header: t(tk.estimates.orderNumber),
      renderCell: estimateItem => estimateItem.orderNumber,
    },
    {
      header: t(tk.estimates.purchaseNetTotal),
      renderCell: estimateItem =>
        numberFormatter(estimateItem.purchaseNetTotal || '0', true),
      tdCssClass: 'amounts-align-right',
    },
    {
      header: t(tk.contract.notAssignedAmount),
      renderCell: estimateItem =>
        numberFormatter(getNotAssignedAmount(estimateItem), true),
      tdCssClass: 'amounts-align-right',
    },
    {
      header: (
        <OverlayTrigger
          overlay={<Tooltip>{t(tk.contract.fillAllFields)}</Tooltip>}
        >
          <Button
            onClick={props.handleFillAllAmounts}
            variant="outline-secondary"
            disabled={props.readOnly}
          >
            <Icon.ChevronDoubleRight size={10} />
          </Button>
        </OverlayTrigger>
      ),
      renderCell: (estimateItem, contractEstimateItem, savedItem) => (
        <OverlayTrigger
          overlay={<Tooltip>{t(tk.contract.fillNotAssignedAmount)}</Tooltip>}
        >
          <Button
            onClick={() =>
              props.handleAmountChange(
                estimateItem.id,
                contractEstimateItem,
                mathUtils
                  .round(
                    mathUtils.add(
                      getNotAssignedAmount(estimateItem),
                      savedItem?.amount || '0',
                    ),
                  )
                  .toString(),
              )
            }
            variant="outline-secondary"
            disabled={props.readOnly}
          >
            <Icon.ChevronRight size={10} />
          </Button>
        </OverlayTrigger>
      ),
    },
    {
      header: t(tk.common.amount),
      renderCell: (estimateItem, contractEstimateItem) => (
        <CurrencyControl
          value={contractEstimateItem?.amount}
          decimalScale={2}
          disabled={props.readOnly}
          onChange={value =>
            props.handleAmountChange(
              estimateItem.id,
              contractEstimateItem,
              value,
            )
          }
          allowNegative
        />
      ),
    },
  ];

  return (
    <>
      <Table striped>
        <TableHead columns={columns} />
        <tbody>
          {props.availableEstimateItems
            .sort((a, b) =>
              (
                momencik(a.estimateByMonth?.date, 'YYYY-MM-DD') +
                a.position.name +
                a.id
              ).localeCompare(
                momencik(b.estimateByMonth?.date, 'YYYY-MM-DD') +
                  b.position.name +
                  b.id,
                'pl',
              ),
            )
            .map(estimateItem => (
              <ContractEstimateItemGridRow
                key={estimateItem.id}
                estimateItem={estimateItem}
                columns={columns}
                extendedContract={props.extendedContract}
                contractEstimateItems={props.contractEstimateItems}
                availableEstimateItems={props.availableEstimateItems}
              />
            ))}
          <ContractEstimateItemPositionsGridSum sumData={props.sumData} />
        </tbody>
      </Table>
      {!!props.warnings.length && (
        <Alert variant="danger">{props.warnings}</Alert>
      )}
    </>
  );
};
